import React, {useRef, useState} from 'react';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import ReCAPTCHA from 'react-google-recaptcha';

declare global {
    interface Window {
        grecaptcha: any;
    }
}

export default function NewsletterSignupMUI({
                                                apiUrl = '/api/newsletter/subscribe',
                                                recaptchaSiteKey,
                                                successMessage = 'Bitte bestätige deine Anmeldung in der E‑Mail, die wir dir gerade geschickt haben.',
                                                title = 'Newsletter abonnieren',
                                                compact = false,
                                            }: {
    apiUrl?: string;
    recaptchaSiteKey?: string;
    successMessage?: string;
    title?: string;
    compact?: boolean;
}) {
    // Clean state initialization - no persistence
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        consent: false
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState<{ type: 'success' | 'error' | ''; msg: string }>({
        type: '',
        msg: ''
    });
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);

    const validate = () => {
        const e: Record<string, string> = {};
        if (!form.firstName.trim()) e.firstName = 'Bitte Vornamen angeben.';
        if (!form.lastName.trim()) e.lastName = 'Bitte Nachnamen angeben.';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Bitte gültige E‑Mail eingeben.';
        if (!form.consent) e.consent = 'Bitte stimme dem Erhalt des Newsletters zu.';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onChange = (field: 'firstName' | 'lastName' | 'email' | 'consent') => (evt: any) => {
        const value = field === 'consent' ? evt.target.checked : evt.target.value;
        setForm((f) => ({...f, [field]: value}));
    };

    const handleSubmit = async (evt: React.FormEvent) => {
        evt.preventDefault();
        setStatus({type: '', msg: ''});

        if (!validate()) return;

        let recaptchaToken: string | null = '';

        // Execute reCAPTCHA if available
        if (recaptchaSiteKey && recaptchaRef.current) {
            try {
                recaptchaToken = await recaptchaRef.current.executeAsync();
                recaptchaRef.current.reset();
            } catch (err) {
                setStatus({
                    type: 'error',
                    msg: 'reCAPTCHA konnte nicht geladen werden. Bitte neu versuchen.'
                });
                return;
            }
        }

        setSubmitting(true);

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({...form, recaptchaToken}),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(data?.error || 'Anmeldung fehlgeschlagen');
            }

            setStatus({type: 'success', msg: successMessage});
            setForm({firstName: '', lastName: '', email: '', consent: false});

        } catch (err: any) {
            setStatus({
                type: 'error',
                msg: err?.message || 'Anmeldung fehlgeschlagen'
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                backgroundColor: 'transparent',
                p: compact ? 1 : 2,
                width: '100%',
                maxWidth: 520,
                boxSizing: 'border-box',
                m: 0,
            }}
        >
            <Typography variant={compact ? 'h6' : 'h5'} sx={{mb: compact ? 1 : 2}}>
                {title}
            </Typography>

            {status.msg && (
                <Alert severity={status.type === 'success' ? 'success' : 'error'} sx={{mb: 2}}>
                    {status.msg}
                </Alert>
            )}

            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: compact ? 1 : 2}}>
                <TextField label="Vorname" variant="outlined" value={form.firstName} onChange={onChange('firstName')}
                           error={!!errors.firstName} helperText={errors.firstName} fullWidth
                           size={compact ? 'small' : 'medium'} autoComplete="given-name"
                           inputProps={{'aria-label': 'Vorname'}}/>
                <TextField label="Nachname" variant="outlined" value={form.lastName} onChange={onChange('lastName')}
                           error={!!errors.lastName} helperText={errors.lastName} fullWidth
                           size={compact ? 'small' : 'medium'} autoComplete="family-name"
                           inputProps={{'aria-label': 'Nachname'}}/>
            </Box>

            <TextField label="E‑Mail" type="email" variant="outlined" value={form.email} onChange={onChange('email')}
                       error={!!errors.email} helperText={errors.email} fullWidth size={compact ? 'small' : 'medium'}
                       autoComplete="email" inputProps={{'aria-label': 'E-Mail-Adresse'}} sx={{mt: compact ? 1 : 2}}/>

            <FormControlLabel control={<Checkbox checked={form.consent} onChange={onChange('consent')}/>}
                              label={<Typography variant="body2" sx={{my: compact ? 0.5 : 1.5}}>Ich willige ein, E-Mails
                                  vom Absender zu erhalten. Ich
                                  kann meine Einwilligung jederzeit widerrufen.</Typography>}
                              sx={{mt: compact ? 0.5 : 1.5}}/>
            {errors.consent && <Typography variant="caption" color="error"
                                           sx={{display: 'block', mt: -1, mb: 1}}>{errors.consent}</Typography>}

            <Typography variant="caption"
                        sx={{display: 'block', opacity: 0.8, mb: compact ? 1 : 2}}>
                Hinweis zu Brevo (Sendinblue): Wir nutzen Brevo als Dienst, um E‑Mails zu versenden und Kontakte zu
                verwalten. Mit deiner Anmeldung bestätigst du, dass deine Daten an Brevo zur Verarbeitung gemäß deren <a
                href="https://www.brevo.com/de/legal/privacypolicy/" target="_blank"
                rel="noreferrer">Datenschutzerklärung</a> übertragen werden.
            </Typography>

            <Box sx={{position: 'relative', display: 'inline-flex'}}>
                <Button type="submit" variant="contained" style={{
                    backgroundColor: 'rgb(213, 44, 11)',
                    color: 'white',
                    minHeight: compact ? 20 : 24,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12
                }}
                        disabled={submitting}>{submitting ? 'Senden...' : 'Jetzt abonnieren'}
                    {submitting && <CircularProgress size={18} sx={{
                        color: 'white',
                        position: 'relative',
                    }}/>}</Button>
            </Box>

            {recaptchaSiteKey && (<ReCAPTCHA sitekey={recaptchaSiteKey} size="invisible" ref={recaptchaRef}/>)}

            {/* Legal footer with logo and links */}
            <Box sx={{
                mt: compact ? 2 : 3,
                pt: compact ? 1 : 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
            }}>
                {/* Left side: Copyright */}
                <Typography variant="caption" sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    © {2025 < new Date().getFullYear() ? `2025-${new Date().getFullYear()}` : new Date().getFullYear()} Medienprojekt
                    Köln e.V.
                </Typography>

                {/* Center: Legal links */}
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    order: {xs: 3, sm: 2},
                    width: {xs: '100%', sm: 'auto'},
                    justifyContent: {xs: 'center', sm: 'flex-start'}
                }}>
                    <Typography
                        component="a"
                        href="https://www.medienprojektkoeln.de/impressum"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            textDecoration: 'none',
                            fontSize: '0.75rem',
                            '&:hover': {
                                textDecoration: 'underline',
                                color: 'primary.main'
                            }
                        }}
                    >
                        Impressum
                    </Typography>
                    <Typography
                        component="a"
                        href="https://www.medienprojektkoeln.de/datenschutz"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            textDecoration: 'none',
                            fontSize: '0.75rem',
                            '&:hover': {
                                textDecoration: 'underline',
                                color: 'primary.main'
                            }
                        }}
                    >
                        Datenschutz
                    </Typography>
                </Box>

                {/* Right side: Logo */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    order: {xs: 2, sm: 3}
                }}>
                    <Typography
                        component="a"
                        href="https://www.medienprojektkoeln.de"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            textDecoration: 'none',
                            pb: '5px',
                            '&:hover': {
                                opacity: 0.8
                            }
                        }}
                    >
                        <img
                            src="https://mpkmail.mrjustinti.me/mpk.png"
                            width="60"
                            style={{
                                display: 'inline-block',
                                lineHeight: 0,
                                verticalAlign: 'bottom'
                            }}
                            alt="Medienprojekt Köln e.V."
                        />
                    </Typography>
                </Box>
            </Box>
        </Box>

    );
}